import type { Project } from '@hangar/shared';
import type { Profile } from '@/api/schemas';
import { cn } from '@/lib/utils';
import { CompactTable, type CompactColumn } from '@/components/CompactTable';
import type { RowAction } from '@/components/RowActions';
import { Icon } from '@/components/Icon';
import { EXECUTION_MODE_LABEL, PlatformIcon, Tag } from '@/components/badges';
import { bindingEnvironment, bindingOverride, bindingSlug, buildTarget, environmentTone, executionLabel } from './profileListModel';

interface Props {
  profiles: Profile[];
  project: Project;
  actionsFor: (profile: Profile) => RowAction[];
}

/** Step 4's table: profile and timeout, platform and artifact, the eas.json profile or Bitrise workflows, environments; Edit · Duplicate · Archive. */
export function ProfilesTable({ profiles, project, actionsFor }: Props) {
  const columns: CompactColumn<Profile>[] = [
    { key: 'profile', header: 'Profile', cell: (p) => <ProfileName p={p} />, className: 'max-w-0', width: 'w-[34%]' },
    { key: 'platform', header: 'Platform', cell: (p) => <ProfilePlatform p={p} /> },
    { key: 'eas', header: 'Builds with', cell: (p) => <span className="font-mono text-xs text-foreground">{buildTarget(p) ?? <span className="text-subtle">—</span>}</span>, hide: '@max-[34rem]:hidden' },
    { key: 'envs', header: 'Environments', cell: (p) => <ProfileEnvironments p={p} project={project} />, className: 'whitespace-normal', hide: '@max-[28rem]:hidden' },
  ];
  return (
    <CompactTable
      label="Build profiles"
      rows={profiles}
      columns={columns}
      rowKey={(p) => p.id}
      rowTestId={(p) => `gs-profile-${p.platform}-${p.slug}`}
      subject={(p) => p.name}
      actions={actionsFor}
      actionsVariant="icon"
      phoneRow={(p) => <PhoneRow p={p} project={project} />}
    />
  );
}

function PhoneRow({ p, project }: { p: Profile; project: Project }) {
  return (
    <span className="flex min-w-0 flex-col gap-1.5">
      <ProfileName p={p} />
      <span className="flex flex-wrap items-center gap-2 font-mono text-xs text-subtle">
        <ProfilePlatform p={p} />
        <span>{buildTarget(p) ?? '—'}</span>
        <span>{p.executionModes.map((m) => EXECUTION_MODE_LABEL[m]).join(', ')}</span>
      </span>
      <ProfileEnvironments p={p} project={project} />
    </span>
  );
}

function ProfileName({ p }: { p: Profile }) {
  return (
    <span className="flex min-w-0 flex-col">
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-[13px] font-medium text-foreground" title={p.name}>
          {p.name}
        </span>
        {/* A compact marker (the full badge does not fit the narrow column): hangar.yml profiles are read-only. */}
        {p.definedIn === 'config_file' ? <Icon name="lock" size={13} label="Managed by hangar.yml" className="shrink-0 text-subtle" /> : null}
      </span>
      <span className="truncate font-mono text-xs text-subtle">
        {p.slug} · {p.timeoutMinutes} min · {executionLabel(p)}
      </span>
    </span>
  );
}

function ProfilePlatform({ p }: { p: Profile }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] text-foreground">
      <PlatformIcon platform={p.platform} size={16} />
      <span className="font-mono text-xs uppercase text-muted-foreground">{p.artifactKind}</span>
    </span>
  );
}

function ProfileEnvironments({ p, project }: { p: Profile; project: Project }) {
  if (p.environments.length === 0) return <span className="text-xs text-subtle">none</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {p.environments.map((b) => {
        const override = bindingOverride(p, b, bindingEnvironment(project, b));
        return (
          <Tag key={b.environmentId} className={cn('max-w-full', environmentTone(bindingEnvironment(project, b)?.kind))}>
            <span className="truncate">
              {bindingSlug(project, b)}
              {override ? ` → ${override}` : ''}
            </span>
          </Tag>
        );
      })}
    </span>
  );
}
